import { useEffect, useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { GripVertical, ArrowUpToLine } from "lucide-react";
import { reorderSlittingPlans } from "@/lib/api";

const DragItem = ({ id, testId, onDrop, onTop, isFirst, children }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item value={id} dragListener={false} dragControls={controls} onDragEnd={onDrop} className="flex items-stretch gap-1 list-none">
      <div
        data-testid={`drag-handle-${testId}`}
        title="Drag to change priority"
        onPointerDown={(e) => controls.start(e)}
        style={{ touchAction: "none" }}
        className="w-7 shrink-0 flex items-center justify-center rounded-sm border border-slate-200 bg-white text-slate-400 hover:text-slate-900 hover:border-slate-900 cursor-grab active:cursor-grabbing transition-colors"
      >
        <GripVertical className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">{children}</div>
      <button
        type="button"
        data-testid={`move-top-${testId}`}
        title="Move to top (priority 1)"
        disabled={isFirst}
        onClick={onTop}
        className="w-7 shrink-0 flex items-center justify-center rounded-sm border border-slate-200 bg-white text-slate-400 hover:text-slate-900 hover:border-slate-900 disabled:opacity-30 disabled:pointer-events-none transition-colors"
      >
        <ArrowUpToLine className="h-4 w-4" />
      </button>
    </Reorder.Item>
  );
};

// Pending plans list with drag-to-reorder; dropping renumbers every open plan's coil priorities.
export const DraggablePlanList = ({ plans, renderRow }) => {
  const qc = useQueryClient();
  const [order, setOrder] = useState(() => plans.map((p) => p.id));
  useEffect(() => setOrder(plans.map((p) => p.id)), [plans]);
  const orderRef = useRef(order);
  orderRef.current = order;
  const byId = Object.fromEntries(plans.map((p) => [p.id, p]));

  const mut = useMutation({
    mutationFn: reorderSlittingPlans,
    onSuccess: () => toast.success("Priority order saved"),
    onError: (e) => { toast.error(e?.response?.data?.detail || "Couldn't save the new order"); setOrder(plans.map((p) => p.id)); },
    onSettled: () => qc.invalidateQueries({ queryKey: ["slitting-plans"] }),
  });

  const commit = () => {
    if (orderRef.current.join() !== plans.map((p) => p.id).join()) mut.mutate(orderRef.current);
  };

  const moveTop = (id) => {
    const next = [id, ...orderRef.current.filter((x) => x !== id)];
    setOrder(next);
    mut.mutate(next);
  };

  return (
    <Reorder.Group axis="y" values={order} onReorder={setOrder} data-testid="plans-list" className="space-y-2">
      {order.filter((id) => byId[id]).map((id, i) => (
        <DragItem key={id} id={id} testId={byId[id].plan_number} onDrop={commit} onTop={() => moveTop(id)} isFirst={i === 0}>
          {renderRow(byId[id])}
        </DragItem>
      ))}
    </Reorder.Group>
  );
};
